import { FormEvent, Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";

import {
  getCatalogAutomation,
  requestCatalogDownload,
  getCatalogUpdateControl,
  getApprovedImageStatus,
  getDashboardSummary,
  getJob,
  getObservability,
  getSession,
  isAuthenticationError,
  login,
  logout,
  pauseCatalogUpdate,
  requestCatalogRehearsal,
  resumeCatalogUpdate,
  recoverCatalogUpdate,
  setCatalogAutomaticApproval,
  setCatalogCompleteCycle,
  startCatalogUpdate,
  approveProductionCycle,
} from "./api";
import type { ApprovedImageStatus, CatalogAutomation, CatalogRehearsalResult, CatalogUpdateControl, DashboardSummary, ObservabilityReport, PortalJob, QualityProfile, SessionUser } from "./types";
import { PolicyConfiguration } from "./PolicyConfiguration";
import { Maintenance } from "./Maintenance";
import { INITIAL_CONTROL, controlChecking, controlFailed, controlPresentation, controlReceived } from "./controlSnapshot";
import { updateTiming } from "./updateTiming";
import aboraLogoLight from "./assets/abora-system-logo-light.png";
import aboraLogoDark from "./assets/abora-system-logo-dark.png";
import { ThemeSwitcher } from "./theme";
import { COUNT_FORMAT } from "./numberFormat";


const EMPTY_SUMMARY: DashboardSummary = {
  total: 0,
  queued: 0,
  running: 0,
  succeeded: 0,
  failed: 0,
  recent: [],
};

const EMPTY_OBSERVABILITY: ObservabilityReport = {
  window_days: 30,
  generated_at: new Date(0).toISOString(),
  overall: {
    completed: 0,
    succeeded: 0,
    failed: 0,
    failure_rate_percent: 0,
    duration: { samples: 0, average_seconds: null, p50_seconds: null, p95_seconds: null, p99_seconds: null, maximum_seconds: null },
  },
  operations: [],
  failures: [],
};

const NAVIGATION_HASHES = new Set(["#resumen", "#automation", "#actualizaciones", "#logs", "#configuracion", "#mantenimiento", "#ayuda"]);
// La ayuda se carga sólo al abrirla (contenido estático, sin llamadas a la API).
const HelpCenter = lazy(() => import("./help/HelpCenter"));
// Capítulo de la ayuda que explica cada sección («#ayuda/<apartado>»).
const HELP_FOR_SECTION: Record<string, string> = {
  "#resumen": "estado",
  "#automation": "cap-automatizar",
  "#actualizaciones": "actualizar",
  "#logs": "logs",
  "#configuracion": "cap-configuracion",
  "#mantenimiento": "espacio",
};
const SECTION_LABELS: Record<string, string> = {
  "#resumen": "Resumen",
  "#automation": "Automatización",
  "#actualizaciones": "Actualizaciones",
  "#logs": "Logs",
  "#configuracion": "Configuración",
  "#mantenimiento": "Mantenimiento",
  "#ayuda": "Ayuda",
};
const WORKFLOW_PHASE_LABELS = {
  acquisition: "Descarga iDirecto",
  preparation: "Validación y preparación",
  catalog_apply: "Aplicación del catálogo",
  images: "Descarga de imágenes",
  final_reconcile: "Reconciliación final",
} as const;

function currentNavigationHash() {
  const hash = window.location.hash;
  // «#ayuda/<apartado>» abre la ayuda en ese apartado.
  if (hash.startsWith("#ayuda/")) return "#ayuda";
  return NAVIGATION_HASHES.has(hash) ? hash : "#resumen";
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Atlantic/Canary",
  }).format(new Date(value));
}

function formatOptionalDate(value: string | null): string {
  return value ? formatDate(value) : "Todavía no disponible";
}

function formatDuration(value: number | null): string {
  if (value === null) return "—";
  if (value === 0) return "0 s";
  if (value < 0) return `−${formatDuration(-value)}`;
  if (value < 1) return `${Math.round(value * 1000)} ms`;
  if (value < 60) return `${value.toLocaleString("es-ES", { maximumFractionDigits: 1 })} s`;
  if (value < 3 * 3600) return `${(value / 60).toLocaleString("es-ES", { maximumFractionDigits: 1 })} min`;
  return `${(value / 3600).toLocaleString("es-ES", { maximumFractionDigits: 1 })} h`;
}

function imageStateLabel(state: string): string {
  return {
    idle: "En espera",
    queued: "En cola",
    running: "En curso",
    succeeded: "Completado",
    failed: "Fallido",
    blocked: "Bloqueado por la fase anterior",
    unknown: "Desconocido",
  }[state] ?? "Desconocido";
}

// Logo sin fondo: la variante clara se usa en fondos claros y la de texto blanco en fondos oscuros (CSS por tema).
function BrandLogo({ className = "" }: { className?: string }) {
  return (
    <span className={`brand-logo ${className}`.trim()} role="img" aria-label="Abora System">
      <img className="brand-logo-light" src={aboraLogoLight} alt="" />
      <img className="brand-logo-dark" src={aboraLogoDark} alt="" />
    </span>
  );
}

const svgProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true, focusable: false };
const TAB_ICONS = {
  "#resumen": <svg {...svgProps}><path d="M3 11 12 4l9 7" /><path d="M5 10v10h14V10" /></svg>,
  "#actualizaciones": <svg {...svgProps}><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v4h-4" /></svg>,
  "#logs": <svg {...svgProps}><path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" /></svg>,
  more: <svg {...svgProps}><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></svg>,
} as const;
const SHEET_ICONS = {
  automation: <svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  configuration: <svg {...svgProps}><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></svg>,
  maintenance: <svg {...svgProps}><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" /></svg>,
  logout: <svg {...svgProps} className="inline-icon"><path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10" /></svg>,
  help: <svg {...svgProps}><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5v-15Z" /><path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20v3H6.5" /><path d="M10 8.5a2 2 0 1 1 2.5 1.9c-.4.2-.5.5-.5.9V12M12 14.5h.01" /></svg>,
} as const;

type Tone = "ok" | "info" | "warn" | "err" | "neutral";

// Texto orientativo derivado del estado ya recibido. No cambia qué acciones están permitidas.
function nextCycleAction(control: CatalogUpdateControl, known: boolean, ready: boolean, isStaff: boolean): { tone: Tone; title: string; text: string } | null {
  if (!known || !ready) return null;
  const run = control.run;
  if (!run) {
    if (control.runtime?.ready === false) return { tone: "warn", title: "No se puede iniciar ahora", text: "El motor de actualización no está preparado. Revisa el aviso del motor antes de continuar." };
    return isStaff
      ? { tone: "info", title: "No hay ningún ciclo en curso", text: "«Iniciar actualización» descarga el CSV de iDirecto y prepara un ciclo nuevo con su simulación y validaciones." }
      : { tone: "neutral", title: "No hay ningún ciclo en curso", text: "Iniciar una actualización requiere permisos de staff." };
  }
  switch (run.status) {
    case "queued": return { tone: "info", title: "Ciclo en cola", text: "Empezará cuando un ejecutor lo recoja. No hace falta intervenir." };
    case "running": return { tone: "ok", title: "No hace falta intervenir", text: "El ciclo avanza solo. Si necesitas pararlo, «Detener de forma segura» lo pausa en un punto seguro y después podrás reanudarlo." };
    case "pause_requested": return { tone: "warn", title: "Detención en curso", text: "El ciclo se detendrá en el siguiente punto seguro. No hace falta volver a pulsar." };
    case "paused": return { tone: "warn", title: "Ciclo en pausa", text: "«Reanudar» continúa desde el último punto seguro." };
    case "waiting_approval": return { tone: "warn", title: "Requiere aprobación", text: "El ciclo espera la aprobación de su simulación exacta. Revísala antes de continuar." };
    case "failed": return { tone: "err", title: "Requiere revisión", text: "Consulta Logs antes de actuar. «Reintentar» conserva este ciclo y su avance; «Reiniciar desde cero» crea un ciclo nuevo." };
    case "succeeded": return { tone: "ok", title: "Ciclo terminado", text: "No quedan acciones pendientes en este ciclo. Revisa los avisos, si los hay." };
    case "cancelled": return { tone: "neutral", title: "Ciclo cancelado", text: "Puedes iniciar un ciclo nuevo cuando lo necesites." };
    default: return null;
  }
}

type CycleRun = NonNullable<CatalogUpdateControl["run"]>;
type StepState = "done" | "current" | "failed" | "paused" | "todo";

// Pasos visibles del ciclo actual. Agrupan `run.phase` en el orden en que el backend declara sus fases.
const CYCLE_STEPS: ReadonlyArray<{ label: string; phases: ReadonlyArray<CycleRun["phase"]> }> = [
  { label: "Descarga iDirecto", phases: ["acquisition"] },
  { label: "Validación", phases: ["profile", "presence", "simulation", "approval", "change_set", "dry_run"] },
  { label: "Aplicación del catálogo", phases: ["apply", "reconcile"] },
  { label: "Exposición y búsqueda", phases: ["expose"] },
  { label: "Imágenes", phases: ["image_profile", "images"] },
  { label: "Reconciliación final", phases: ["finalize"] },
];

function cycleSteps(run: CycleRun): StepState[] | null {
  const current = run.phase === "complete" ? CYCLE_STEPS.length : CYCLE_STEPS.findIndex(step => step.phases.includes(run.phase));
  if (current < 0) return null;
  return CYCLE_STEPS.map((_, index) => {
    if (run.status === "succeeded" || index < current) return "done";
    if (index > current) return "todo";
    if (run.status === "failed") return "failed";
    if (["paused", "pause_requested", "waiting_approval", "cancelled"].includes(run.status)) return "paused";
    return "current";
  });
}

function stepStateLabel(state: StepState, run: CycleRun, confirmed: boolean): string {
  if (state === "done") return "Completada";
  if (state === "todo") return "Pendiente";
  if (state === "failed") return "Fallida";
  if (state === "paused") return run.status === "waiting_approval" ? "Esperando aprobación" : run.status === "cancelled" ? "Cancelada" : "En pausa";
  return confirmed ? "En curso" : "En curso (sin confirmar)";
}

// Estado del chip de ciclo; mismas equivalencias que la tarjeta de control.
function cycleStateClass(run: CatalogUpdateControl["run"]): string {
  const status = run?.status;
  return status === "pause_requested" || status === "paused" || status === "waiting_approval" ? "queued" : status ?? "idle";
}

// Titular del Resumen derivado del estado ya recibido.
function cycleHeadline(control: CatalogUpdateControl, known: boolean, state: "loading" | "ready" | "error"): string {
  if (!known) return state === "error" ? "Estado del ciclo no disponible" : "Consultando el estado del ciclo";
  if (state === "error") return "Estado sin confirmar · se muestra el último conocido";
  switch (control.run?.status) {
    case undefined: return "No hay ningún ciclo en curso";
    case "queued": return "Ciclo en cola · empezará cuando lo recoja un ejecutor";
    case "running": return "Actualización en curso · sin intervención necesaria";
    case "pause_requested": return "Detención en curso · se parará en un punto seguro";
    case "paused": return "Ciclo en pausa";
    case "waiting_approval": return "El ciclo espera aprobación";
    case "failed": return "El ciclo se ha detenido por un error · revisa Actualizaciones";
    case "succeeded": return "Último ciclo terminado";
    case "cancelled": return "Ciclo cancelado";
    default: return control.run?.status_label ?? "Estado del ciclo";
  }
}

function priceWarningLabel(code: string): string {
  return code === "COMMERCIAL_OVER_20_PERCENT_EXCEEDED" ? "Productos con variación ≥ 20 %" : code === "COMMERCIAL_OVER_50_EUR_EXCEEDED" ? "Productos con variación ≥ 50 €" : "Variación agregada absoluta (%)";
}

function cycleWarningCount(run: CatalogUpdateControl["run"]): number {
  return (run?.price_warnings?.length ? 1 : 0) + ((run?.protected_products_skipped ?? 0) > 0 ? 1 : 0) + ((run?.image_quarantined_rows ?? 0) > 0 ? 1 : 0);
}

const WarnIcon = () => <svg {...svgProps} className="inline-icon"><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4M12 17h.01" /></svg>;
const InfoIcon = () => <svg {...svgProps} className="inline-icon"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>;
const ClockIcon = () => <svg {...svgProps} className="inline-icon"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
const ShieldIcon = () => <svg {...svgProps} className="inline-icon"><path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3Z" /></svg>;

// Avisos no bloqueantes del ciclo actual (mismos datos que antes, agrupados en su propia tarjeta).
function CycleWarnings({ run, known }: { run: CatalogUpdateControl["run"]; known: boolean }) {
  const count = cycleWarningCount(run);
  const skipped = run?.protected_products_skipped ?? 0;
  return (
    <section className="card-block cycle-warnings" aria-label="Avisos de este ciclo">
      <div className="card-block-heading">
        <h2>Avisos de este ciclo</h2>
        {known && run ? <span className={`soft-chip ${count ? "chip-warn" : "chip-ok"}`}>{count ? <><WarnIcon />{count} {count === 1 ? "aviso" : "avisos"} · ninguno bloquea</> : "Sin avisos"}</span> : null}
      </div>
      {!known ? <p className="card-block-empty">Se mostrarán cuando el servidor confirme el estado del ciclo.</p>
        : !run ? <p className="card-block-empty">No hay ningún ciclo en curso.</p>
          : count === 0 ? <p className="card-block-empty">Este ciclo no tiene avisos de precios ni productos protegidos omitidos.</p> : null}
      {run?.price_warnings?.length ? <div role="status" className="snapshot-alert warning with-icon">
        <WarnIcon />
        <div>
          <strong>Aviso de variación de precios · no bloquea la actualización</strong>
          <p>El CSV iDirecto es la fuente de precios; se aplican los márgenes e impuestos configurados. Se mantienen los controles de datos y seguridad.</p>
          <ul>{run.price_warnings.map(warning => <li key={warning.code}>{priceWarningLabel(warning.code)}: {warning.actual} · umbral de aviso: {warning.limit}</li>)}</ul>
        </div>
      </div> : null}
      {skipped > 0 ? <div role="status" className="snapshot-alert with-icon">
        <InfoIcon />
        <div>
          <strong>{skipped.toLocaleString("es-ES", COUNT_FORMAT)} productos protegidos omitidos</strong>
          <p>Manuales, de origen desconocido o con protección explícita. Se conservan sus contenidos, stock, publicación e imágenes; el resto del catálogo continúa.</p>
        </div>
      </div> : null}
      {(run?.image_quarantined_rows ?? 0) > 0 ? <p role="status" className="snapshot-alert warning">{run?.image_quarantined_rows} productos sin una portada de proveedor disponible. El catálogo continúa; se conservan las imágenes anteriores y quedan registrados los avisos.</p> : null}
    </section>
  );
}

function LoginScreen({ onAuthenticated, initialError = "" }: { onAuthenticated: (user: SessionUser) => void; initialError?: string }) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState(initialError);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordVisible(false);
    setSubmitting(true);
    setError("");
    try {
      onAuthenticated(await login(identifier, password));
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "No se pudo iniciar sesión.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-brand" aria-label="Presentación del portal">
        <BrandLogo />
        <h1>Portal del empleado</h1>
      </section>

      <section className="login-panel">
        <ThemeSwitcher className="login-theme" />
        <form className="login-card" onSubmit={submit}>
          <div>
            <h2>Bienvenido de nuevo</h2>
            <p className="muted">Accede con tu cuenta interna.</p>
          </div>
          <label>
            Usuario
            <input
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              required
            />
          </label>
          <div className="password-field">
            <label htmlFor="login-password">Contraseña</label>
            <div className="password-input-wrap">
              <input
                id="login-password"
                type={passwordVisible ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <button
                className="password-visibility-toggle"
                type="button"
                aria-label={passwordVisible ? "Ocultar contraseña" : "Mostrar contraseña"}
                title={passwordVisible ? "Ocultar contraseña" : "Mostrar contraseña"}
                aria-controls="login-password"
                onClick={() => setPasswordVisible((visible) => !visible)}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
                  <circle cx="12" cy="12" r="3" />
                  {passwordVisible ? <path d="m3 3 18 18" /> : null}
                </svg>
              </button>
            </div>
          </div>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="primary-button" disabled={submitting} type="submit">
            {submitting ? "Comprobando…" : "Entrar al portal"}
          </button>
          <p className="login-note">Este acceso no comparte sesión ni credenciales con PrestaShop.</p>
        </form>
      </section>
    </main>
  );
}

function StatusPill({ status }: { status: PortalJob["status"] }) {
  const labels: Record<PortalJob["status"], string> = {
    queued: "En cola",
    running: "En curso",
    succeeded: "Completado",
    failed: "Fallido",
  };
  return <span className={`status-pill status-${status}`}>{labels[status]}</span>;
}

function Dashboard({ user, onLogout, onSessionExpired }: { user: SessionUser; onLogout: () => void; onSessionExpired: () => void }) {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [automation, setAutomation] = useState<CatalogAutomation | null>(null);
  const [controlSnapshot, setControlSnapshot] = useState(INITIAL_CONTROL);
  const updateControl: CatalogUpdateControl = controlSnapshot.data ?? { active: false, run: null };
  const productionProfile = updateControl.environment === "production";
  const automationControlsBlocked = productionProfile && automation?.production_controls?.available !== true;
  const environmentLabel = productionProfile ? "Perfil productivo" : "VM TEST";
  const controlView = controlPresentation(controlSnapshot);
  const controlRequest = useRef<Promise<void> | null>(null);
  const controlVersion = useRef(0);
  const controlMutation = useRef(false);
  const [observability, setObservability] = useState<ObservabilityReport>(EMPTY_OBSERVABILITY);
  const [imageStatus, setImageStatus] = useState<ApprovedImageStatus>({
    schema: "abora-catalog-workflow-public-status-v1",
    available: false,
    reason: "not_published",
  });
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<PortalJob | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [rehearsalSubmitting, setRehearsalSubmitting] = useState(false);
  const [updateControlBusy, setUpdateControlBusy] = useState(false);
  const [automationControlBusy, setAutomationControlBusy] = useState(false);
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);
  const [downloadJob, setDownloadJob] = useState<PortalJob | null>(null);
  const [downloadError, setDownloadError] = useState("");
  const [configurationFocus, setConfigurationFocus] = useState<{
    policyId: string;
    evidenceId: string;
  } | null>(null);
  const [activeSection, setActiveSection] = useState(currentNavigationHash);
  const [advancedUpdates, setAdvancedUpdates] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuButton = useRef<HTMLButtonElement | null>(null);
  const mobileMenuClose = useRef<HTMLButtonElement | null>(null);
  const cycleTiming = updateTiming(controlSnapshot, imageStatus);

  useEffect(() => {
    if (automation?.latest_download && (!downloadJob || automation.latest_download.created_at >= downloadJob.created_at)) {
      setDownloadJob(automation.latest_download);
    }
  }, [automation]);

  async function downloadCatalogNow() {
    if (downloadSubmitting) return;
    setDownloadSubmitting(true);
    setDownloadError("");
    try {
      setDownloadJob(await requestCatalogDownload());
      setAutomation(await getCatalogAutomation());
    } catch (error) {
      if (isAuthenticationError(error)) onSessionExpired();
      setDownloadError(error instanceof Error ? error.message : "No se pudo solicitar el CSV.");
    } finally {
      setDownloadSubmitting(false);
    }
  }

  useEffect(() => {
    if (!downloadJob || !["queued", "running"].includes(downloadJob.status)) return;
    let disposed = false;
    let pending = false;
    const timer = window.setInterval(async () => {
      if (pending) return;
      pending = true;
      try {
        const job = await getJob(downloadJob.id);
        if (!disposed) { setDownloadJob(job); setDownloadError(""); }
      } catch (error) {
        if (isAuthenticationError(error)) onSessionExpired();
        if (!disposed) setDownloadError("No se pudo consultar la descarga. No se ha cancelado; consulta Logs.");
      } finally { pending = false; }
    }, 3000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [downloadJob, onSessionExpired]);

  const refreshControl = useCallback(() => {
    if (controlMutation.current) return Promise.resolve();
    // Coalesce polling and manual refresh; a slow endpoint must not pile up GETs.
    if (controlRequest.current) return controlRequest.current;
    const version = controlVersion.current;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    controlRequest.current = getCatalogUpdateControl(controller.signal).then(data => {
      if (version === controlVersion.current) setControlSnapshot(controlReceived(data, new Date().toISOString()));
    }).catch(error => {
      // A command supersedes older reads, including their failures. Authentication
      // errors still expire the session even when the request became obsolete.
      if (version !== controlVersion.current && !isAuthenticationError(error)) return;
      if (version === controlVersion.current) setControlSnapshot(controlFailed);
      if (isAuthenticationError(error)) onSessionExpired();
      throw error;
    }).finally(() => {
      window.clearTimeout(timeout);
      controlRequest.current = null;
    });
    return controlRequest.current;
  }, [onSessionExpired]);

  const refresh = useCallback(async () => {
    const tasks = [
      { label: "resumen", run: async () => setSummary(await getDashboardSummary()) },
      { label: "automatización", run: async () => setAutomation(await getCatalogAutomation()) },
      { label: "observabilidad", run: async () => setObservability(await getObservability()) },
      { label: "imágenes", run: async () => setImageStatus(await getApprovedImageStatus()) },
      { label: "control del ciclo", run: refreshControl },
    ];
    const results = await Promise.allSettled(tasks.map(({ run }) => run()));
    const failures = results
      .map((result, index) => ({ result, label: tasks[index].label }))
      .filter((entry): entry is { result: PromiseRejectedResult; label: string } => entry.result.status === "rejected");

    if (failures.some(({ result }) => isAuthenticationError(result.reason))) {
      onSessionExpired();
      return;
    }

    if (failures.length > 0) {
      setError(`No se pudo actualizar: ${failures.map(({ label }) => label).join(", ")}. Los demás indicadores siguen activos.`);
    } else {
      setError("");
    }
    setLoading(false);
  }, [onSessionExpired, refreshControl]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    const updateActiveSection = () => {
      setActiveSection(currentNavigationHash());
      setSelectedJob(null);
      setMobileMenuOpen(false);
      // Dentro de la ayuda, «#ayuda/<apartado>» se desplaza al apartado (lo gestiona la propia ayuda).
      if (!window.location.hash.startsWith("#ayuda/")) window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    };
    window.addEventListener("hashchange", updateActiveSection);
    return () => window.removeEventListener("hashchange", updateActiveSection);
  }, []);

  // Menú móvil: sólo presentación. Escape lo cierra y el foco vuelve al botón «Más».
  useEffect(() => {
    if (!mobileMenuOpen) return;
    mobileMenuClose.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileMenuOpen(false);
      mobileMenuButton.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileMenuOpen]);

  async function openJob(jobId: string) {
    setDetailLoading(true);
    setError("");
    try {
      setSelectedJob(await getJob(jobId));
    } catch (detailError) {
      setError(detailError instanceof Error ? detailError.message : "No se pudo abrir el informe.");
    } finally {
      setDetailLoading(false);
    }
  }

  async function prepareRehearsal() {
    setRehearsalSubmitting(true);
    setError("");
    try {
      await requestCatalogRehearsal();
      await refresh();
    } catch (rehearsalError) {
      setError(rehearsalError instanceof Error ? rehearsalError.message : "No se pudo preparar el ensayo.");
    } finally {
      setRehearsalSubmitting(false);
    }
  }

  async function controlCatalogUpdate(action: "start" | "pause" | "resume" | "retry" | "restart" | "approve") {
    if (controlView.actionsBlocked || updateControlBusy || controlMutation.current || !user.isStaff) return;
    if (action === "restart" && !window.confirm(
      "¿Reiniciar desde cero? Se detendrá el ciclo actual en un punto seguro y se creará otro con nueva descarga CSV, simulación y validaciones. No se borrarán productos, imágenes ni evidencias anteriores."
    )) return;
    const productionPlan = updateControl.run?.production_plan;
    if (action === "approve" && (!productionPlan?.sha256 || !window.confirm(
      `¿Aprobar este catálogo exacto? ${productionPlan.summary.creates ?? 0} altas, ${productionPlan.summary.updates ?? 0} actualizaciones y ${productionPlan.summary.protected ?? 0} protegidos omitidos. Las imágenes no disponibles quedarán como avisos; no se borrarán imágenes anteriores.`
    ))) return;
    setUpdateControlBusy(true);
    controlMutation.current = true;
    // Ignore any GET that started before this command's authoritative response.
    controlVersion.current += 1;
    setError("");
    try {
      const runId = updateControl.run?.id;
      const next = action === "start"
        ? await startCatalogUpdate()
        : action === "approve" && runId && productionPlan?.sha256
          ? await approveProductionCycle(runId, productionPlan.sha256)
        : action === "pause" && runId
          ? await pauseCatalogUpdate(runId)
          : action === "resume" && runId
            ? await resumeCatalogUpdate(runId)
            : (action === "retry" || action === "restart") && runId
              ? await recoverCatalogUpdate(runId, action)
              : updateControl;
      setControlSnapshot(controlReceived(next, new Date().toISOString()));
      await refresh();
    } catch (controlError) {
      // A rejected command is not a failed status read. Keep actions locked
      // until a fresh GET confirms the state (also for ambiguous POST failures).
      if (isAuthenticationError(controlError)) {
        setControlSnapshot(controlFailed);
        onSessionExpired();
        return;
      }
      setControlSnapshot(controlChecking);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        // Do not coalesce with a GET started before the command: its version
        // was invalidated and its response cannot confirm the resulting state.
        const confirmed = await getCatalogUpdateControl(controller.signal);
        setControlSnapshot(controlReceived(confirmed, new Date().toISOString()));
      } catch (readError) {
        setControlSnapshot(controlFailed);
        if (isAuthenticationError(readError)) onSessionExpired();
      } finally {
        window.clearTimeout(timeout);
      }
      setError(controlError instanceof Error ? controlError.message : "No se pudo cambiar el estado de la actualización.");
    } finally {
      controlMutation.current = false;
      setUpdateControlBusy(false);
    }
  }

  async function changeAutomaticApproval(enabled: boolean) {
    setAutomationControlBusy(true);
    setError("");
    try {
      setAutomation(await setCatalogAutomaticApproval(enabled));
    } catch (automationError) {
      setError(automationError instanceof Error ? automationError.message : "No se pudo cambiar la aprobación automática.");
    } finally {
      setAutomationControlBusy(false);
    }
  }

  async function changeCompleteCycle(settings: { enabled?: boolean; intervalHours?: number }) {
    setAutomationControlBusy(true);
    setError("");
    try {
      setAutomation(await setCatalogCompleteCycle(settings));
    } catch (automationError) {
      setError(automationError instanceof Error ? automationError.message : "No se pudo cambiar la programación del ciclo completo.");
    } finally {
      setAutomationControlBusy(false);
    }
  }

  // Mismas condiciones que antes en cada botón; se nombran para poder explicar el motivo debajo.
  const startDisabled = !user.isStaff || updateControlBusy || updateControl.active || updateControl.runtime?.ready === false;
  const retryDisabled = !user.isStaff || updateControlBusy || !updateControl.run?.can_retry;
  const restartDisabled = !user.isStaff || updateControlBusy || !updateControl.run?.can_restart;
  const pauseDisabled = !user.isStaff || updateControlBusy || !updateControl.run?.can_pause;
  const resumeDisabled = !user.isStaff || updateControlBusy || !updateControl.run?.can_resume;
  const actionReason = (disabled: boolean, whenUnavailable: string, whenAvailable: string) => {
    if (controlView.actionsBlocked) return "Bloqueado hasta confirmar el estado con el servidor.";
    if (!user.isStaff) return "Requiere permisos de staff.";
    if (updateControlBusy) return "Procesando la orden anterior…";
    return disabled ? whenUnavailable : whenAvailable;
  };
  const downloadInProgress = !!(downloadJob && ["queued", "running"].includes(downloadJob.status));
  const downloadAvailable = productionProfile ? automation?.production_controls?.manual_download_available === true : automation?.enabled === true;
  const downloadDisabled = !downloadAvailable || !user.isStaff || automation?.running || updateControl.active || downloadSubmitting || downloadInProgress;
  const downloadReason = !user.isStaff ? "Requiere permisos de staff."
    : !automation ? "Esperando el estado de la automatización."
      : !downloadAvailable ? "La descarga no tiene autorización disponible."
        : automation.running || downloadInProgress || downloadSubmitting ? "Ya hay una descarga pendiente o en curso."
          : updateControl.active ? "Bloqueado mientras hay un ciclo de actualización activo."
            : "Descarga y valida el CSV sin publicar nada.";
  const nextAction = nextCycleAction(updateControl, controlView.known, controlSnapshot.state === "ready", user.isStaff);
  const run = updateControl.run;
  const steps = run ? cycleSteps(run) : null;
  const warningCount = cycleWarningCount(run);
  const freshnessText = controlSnapshot.updatedAt
    ? `${controlSnapshot.state === "error" ? "Sin respuesta nueva desde" : "Datos recibidos"} ${formatDate(controlSnapshot.updatedAt)} · se consulta cada 5 s`
    : controlSnapshot.state === "error" ? "Todavía no hay una respuesta válida del servidor" : "Esperando la primera respuesta del servidor";
  // El detalle de imágenes sólo cuenta como del ciclo actual si coincide su change set (misma regla que la vista avanzada).
  const imagesForCurrentCycle = controlView.known && !!run && imageStatus.available && run.change_set_id?.slice(0, 8) === imageStatus.change_set;

  const jobHistory = <details className="updates-detail-group job-history">
    <summary>Historial de trabajos e informes</summary>
    {loading ? <p className="empty-state">Cargando actividad…</p> : null}
    {!loading && summary.recent.length === 0 ? (
      <div className="empty-state"><strong>Aún no hay trabajos</strong><span>La primera consulta automática aparecerá aquí.</span></div>
    ) : null}
    {summary.recent.length > 0 ? (
      <div className="table-wrap">
        <table>
          <thead><tr><th>Trabajo</th><th>Origen</th><th>Creado</th><th>Estado</th></tr></thead>
          <tbody>
            {summary.recent.map((job) => (
              <tr key={job.id}>
                <td><strong>{job.kind_label}</strong><span className="job-id">{job.id.slice(0, 8)}</span></td>
                <td>{job.input_name || "Sin fichero"}</td>
                <td>{formatDate(job.created_at)}</td>
                <td><StatusPill status={job.status} />{["catalog_profile", "catalog_rehearsal"].includes(job.kind) && job.status === "succeeded" ? <button className="row-action" disabled={detailLoading} onClick={() => void openJob(job.id)}>{job.kind === "catalog_rehearsal" ? "Ver plan" : "Ver informe"}</button> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null}
  </details>;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <BrandLogo />
          <span>Portal del empleado</span>
          <span className="environment-chip"><span className="environment-dot" aria-hidden="true" />{environmentLabel}</span>
        </div>
        <div className="sidebar-menu">
        <nav aria-label="Navegación principal">
          <a className={`nav-item${activeSection === "#resumen" ? " active" : ""}`} href="#resumen" aria-current={activeSection === "#resumen" ? "page" : undefined}><span>01</span> Resumen</a>
          <a className={`nav-item${activeSection === "#automation" ? " active" : ""}`} href="#automation" aria-current={activeSection === "#automation" ? "page" : undefined}><span>02</span> Automatización</a>
          <a className={`nav-item${activeSection === "#actualizaciones" ? " active" : ""}`} href="#actualizaciones" aria-current={activeSection === "#actualizaciones" ? "page" : undefined}><span>03</span> Actualizaciones</a>
          <a className={`nav-item${activeSection === "#logs" ? " active" : ""}`} href="#logs" aria-current={activeSection === "#logs" ? "page" : undefined}><span>04</span> Logs</a>
          {user.isStaff ? <a className={`nav-item${activeSection === "#configuracion" ? " active" : ""}`} href="#configuracion" aria-current={activeSection === "#configuracion" ? "page" : undefined} onClick={() => setConfigurationFocus(null)}><span>05</span> Configuración</a> : <button className="nav-item disabled" disabled><span>05</span> Configuración <em>Staff</em></button>}
          {user.isStaff && <a className={`nav-item${activeSection === "#mantenimiento" ? " active" : ""}`} href="#mantenimiento" aria-current={activeSection === "#mantenimiento" ? "page" : undefined}><span>06</span> Mantenimiento</a>}
          <a className={`nav-item${activeSection === "#ayuda" ? " active" : ""}`} href="#ayuda" aria-current={activeSection === "#ayuda" ? "page" : undefined}><span>07</span> Ayuda</a>
        </nav>
        </div>
        <div className="environment-card">
          <span className="environment-dot" />
          <div><strong>{environmentLabel}</strong><small>{productionProfile ? "Destino ligado al perfil del servidor" : "Sin conexión a PROD"}</small></div>
        </div>
      </aside>
      <div className="mobile-menu-backdrop" hidden={!mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
      <nav className="mobile-tabbar" aria-label="Accesos rápidos">
        {(["#resumen", "#actualizaciones", "#logs"] as const).map((hash) => {
          const label = hash === "#resumen" ? "Resumen" : hash === "#actualizaciones" ? "Actualizaciones" : "Logs";
          const cycleTone = controlSnapshot.state !== "ready" ? "unknown" : updateControl.run?.status === "failed" ? "err" : ["paused", "pause_requested", "waiting_approval"].includes(updateControl.run?.status ?? "") ? "warn" : ["queued", "running"].includes(updateControl.run?.status ?? "") ? "run" : null;
          return <a key={hash} href={hash} aria-current={activeSection === hash ? "page" : undefined}>
            <span className="tab-icon">{TAB_ICONS[hash]}{hash === "#actualizaciones" && cycleTone ? <span className={`tab-dot tab-dot-${cycleTone}`} aria-hidden="true" /> : null}</span>
            <span>{label}</span>
            {hash === "#actualizaciones" && cycleTone ? <span className="sr-only">{`, ${controlView.label}`}</span> : null}
          </a>;
        })}
        <button ref={mobileMenuButton} type="button" aria-expanded={mobileMenuOpen} aria-controls="more-sheet"
          className={["#automation", "#configuracion", "#mantenimiento", "#ayuda"].includes(activeSection) ? "active" : undefined}
          onClick={() => setMobileMenuOpen((open) => !open)}>
          <span className="tab-icon">{TAB_ICONS.more}</span><span>Más</span>
        </button>
      </nav>
      {mobileMenuOpen ? <section className="more-sheet" id="more-sheet" role="dialog" aria-modal="true" aria-labelledby="more-sheet-title">
        <div className="sheet-grip" aria-hidden="true" />
        <div className="sheet-head">
          <h2 id="more-sheet-title">Más secciones</h2>
          <button ref={mobileMenuClose} type="button" className="text-button" onClick={() => { setMobileMenuOpen(false); mobileMenuButton.current?.focus(); }}>Cerrar</button>
        </div>
        <ul className="sheet-list" onClick={(event) => { if ((event.target as HTMLElement).closest("a")) setMobileMenuOpen(false); }}>
          <li><a href="#automation" aria-current={activeSection === "#automation" ? "page" : undefined}>{SHEET_ICONS.automation}<span><strong>Automatización</strong><small>Descarga CSV, precios y programación</small></span></a></li>
          <li>{user.isStaff
            ? <a href="#configuracion" aria-current={activeSection === "#configuracion" ? "page" : undefined} onClick={() => setConfigurationFocus(null)}>{SHEET_ICONS.configuration}<span><strong>Configuración</strong><small>Política, simulación y firmas</small></span></a>
            : <span className="sheet-item-disabled" aria-disabled="true">{SHEET_ICONS.configuration}<span><strong>Configuración</strong><small>Requiere permisos de staff</small></span></span>}</li>
          {user.isStaff ? <li><a href="#mantenimiento" aria-current={activeSection === "#mantenimiento" ? "page" : undefined}>{SHEET_ICONS.maintenance}<span><strong>Mantenimiento</strong><small>Temporales y espacio en disco</small></span></a></li> : null}
          <li><a href="#ayuda" aria-current={activeSection === "#ayuda" ? "page" : undefined}>{SHEET_ICONS.help}<span><strong>Ayuda</strong><small>Guía, tutoriales y simuladores</small></span></a></li>
        </ul>
        <div className="sheet-row"><span className="menu-label">Tema</span><ThemeSwitcher /></div>
        <div className="sheet-row">
          <span className="sheet-user"><span className="avatar" aria-hidden="true">{user.username.slice(0, 1).toUpperCase()}</span><span><strong>{user.username}</strong><small>{user.isStaff ? "Staff" : "Usuario"} · {environmentLabel}</small></span></span>
          <button className="text-button" type="button" onClick={onLogout}>{SHEET_ICONS.logout}Salir</button>
        </div>
      </section> : null}

      <main className="content">
        <header className="topbar">
          <div>
            <p className="eyebrow">{SECTION_LABELS[activeSection] ?? "Resumen"}</p>
            <h1>{activeSection === "#resumen" ? `Buenos días, ${user.username.split("@")[0]}` : activeSection === "#actualizaciones" ? "Actualizaciones de catálogo" : activeSection === "#ayuda" ? "Guía del Portal" : SECTION_LABELS[activeSection]}</h1>
            {HELP_FOR_SECTION[activeSection] ? <a className="topbar-help" href={`#ayuda/${HELP_FOR_SECTION[activeSection]}`}>¿Cómo funciona esta sección? Ver la ayuda</a> : null}
          </div>
          <div className="user-menu">
            <ThemeSwitcher className="topbar-theme" />
            <div className="avatar" aria-hidden="true">{user.username.slice(0, 1).toUpperCase()}</div>
            <div title={user.email || undefined}><strong>{user.username}</strong><span>{user.isStaff ? "Staff" : "Usuario"} · cuenta interna</span></div>
            <button className="text-button" onClick={onLogout}>Salir</button>
          </div>
        </header>

        <section className="safety-banner" aria-label="Estado de seguridad">
          <ShieldIcon />
          <p>{productionProfile ? <><strong>Perfil productivo.</strong> Ejecución ligada al destino y a las reglas autorizadas. La programación y la aprobación automática sólo funcionan si las seleccionas en Automatización.</> : <><strong>Entorno TEST.</strong> El catálogo y sus imágenes se procesan sólo en la VM; no hay conexión con PROD.</>}</p>
          <span className="test-badge">{productionProfile ? "SUPERVISADO" : "TEST"}</span>
        </section>

        {error ? <p className="form-error page-error" role="alert">{error}</p> : null}

        {activeSection === "#resumen" ? <section id="resumen" className="overview route-panel">
          <article className={`card-block now-card update-control-${run?.status ?? "idle"}`} aria-live="polite">
            <div className="card-block-heading">
              <div><p className="eyebrow">Ahora mismo</p><h2>{cycleHeadline(updateControl, controlView.known, controlSnapshot.state)}</h2></div>
              <span className={`image-state image-state-${controlSnapshot.state === "ready" ? cycleStateClass(run) : "idle"}`}>{controlView.label}</span>
            </div>
            <div className="now-body">
              <div>
                {run || !controlView.known ? <>
                  <div className="image-progress-label">
                    <strong>{controlView.title}</strong>
                    <span className="now-percent">{controlView.percent === undefined ? "—" : `${controlView.percent.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %`}</span>
                  </div>
                  <progress max={100} value={controlView.percent} aria-label="Progreso del ciclo actual">{controlView.percent === undefined ? "Consultando estado" : `${controlView.percent} %`}</progress>
                </> : <p className="now-message">{controlView.message}</p>}
                <p className={`control-freshness${controlSnapshot.state === "error" ? " stale" : ""}`}>{freshnessText}</p>
              </div>
              <div className="now-actions">
                <a className="primary-button" href="#actualizaciones">Ver actualización</a>
                <a className="secondary-button" href="#logs">Logs</a>
              </div>
            </div>
          </article>
          <div className="metric-row">
            <article className="metric-card accent"><span>Trabajos totales</span><strong>{summary.total}</strong><small>Registrados en el portal</small></article>
            <article className="metric-card"><span>En curso</span><strong>{summary.queued + summary.running}</strong><small>{summary.queued} en cola</small></article>
            <article className="metric-card"><span>Completados</span><strong>{summary.succeeded}</strong><small>{summary.failed} con fallo</small></article>
            <article className="metric-card"><span>Avisos activos</span><strong>{controlView.known ? warningCount : "—"}</strong><small>{!controlView.known ? "Estado sin confirmar" : warningCount ? "Ninguno bloquea" : "Sin avisos en el ciclo"}</small></article>
          </div>
          <div className="pair-grid">
            <article className="card-block automation-summary">
              <p className="eyebrow">Automatización</p>
              <h2>Descarga del CSV</h2>
              <div className="chip-row">
                <span className={`automation-chip ${automation?.enabled ? "automation-on" : "automation-off"}`}>
                  {automation?.enabled ? `Activa · cada ${automation.interval_minutes} min` : "Automatización inactiva"}
                </span>
                <span className="soft-chip">Ciclos completos programados: {automation?.complete_cycle_blocked ? "bloqueados" : automation?.complete_cycle_enabled ? "activados" : "desactivados"}</span>
              </div>
              <dl className="mini-kv">
                <div><dt>Último éxito</dt><dd>{formatOptionalDate(automation?.last_success_at ?? null)}</dd></div>
                <div><dt>Fallos consecutivos</dt><dd>{automation?.consecutive_failures ?? 0}</dd></div>
              </dl>
              <p className="card-note">Descargar el CSV no publica productos ni inicia un ciclo completo.</p>
              {user.isStaff && !productionProfile ? <button className="secondary-button" disabled={rehearsalSubmitting} onClick={() => void prepareRehearsal()}>{rehearsalSubmitting ? "Preparando…" : "Preparar ensayo mixto TEST"}</button> : null}
            </article>
            <CycleWarnings run={run} known={controlView.known} />
          </div>
          {jobHistory}
        </section> : null}

        {activeSection === "#automation" ? <section id="automation" className="page-section automation-panel route-panel">
          <div className="panel-heading">
            <div><p className="eyebrow">Automatización segura</p><h2>Catálogo y precios de referencia</h2></div>
            <span className="test-badge"><ShieldIcon />No publica</span>
          </div>
          <article className="manual-download-card" aria-live="polite">
              {productionProfile ? <p role="status">Puedes iniciar manualmente desde <a href="#actualizaciones">Actualizaciones</a> o seleccionar las opciones productivas de esta página. No se activa nada por abrirla. La descarga independiente TEST no se utiliza en PROD.</p> : null}
              <div className="manual-download-text">
                <strong>Descarga manual del CSV</strong>
                <p>Sólo descarga y valida la fuente de iDirecto en el servidor. No publica productos ni inicia un ciclo completo.</p>
              </div>
              <div className="manual-download-action">
                <button type="button" className="primary-button compact" onClick={() => void downloadCatalogNow()} aria-describedby="download-reason"
                  disabled={downloadDisabled}>
                  {downloadSubmitting ? "Solicitando…" : automation?.running ? "Descarga pendiente o en curso…" : "Descargar CSV ahora"}
                </button>
                <small id="download-reason">{downloadReason}</small>
              </div>
              {downloadJob ? <p role="status">{downloadJob.status === "queued" ? "Solicitud en cola; el procesador la recogerá en unos segundos." : downloadJob.status === "running" ? "Descargando y validando el CSV…" : downloadJob.status === "succeeded" ? `Última descarga correcta: ${formatOptionalDate(downloadJob.finished_at)}. ${downloadJob.result?.changed === false ? "Contenido sin cambios; se conserva el archivo anterior." : "Puedes generar una nueva simulación en Configuración."}` : `La descarga ha fallado: ${downloadJob.error_code || "consulta Logs"}.`} <a href="#logs">Ver Logs</a></p> : null}
              {downloadError ? <p role="alert">{downloadError}</p> : null}
          </article>
          <div className="automation-grid" aria-live="polite">
            {productionProfile ? <p className="readonly-note">{automation?.production_controls?.available ? automation.production_controls.scope === "recurring-production" ? "Operación productiva recurrente autorizada para esta configuración. La programación sólo se activa al seleccionarla; sin solapes ni repetición automática de fallos. La descarga manual no publica productos." : `Autorización acotada: ${automation.production_controls.remaining_cycles ?? 0} de ${automation.production_controls.maximum_cycles ?? 0} ciclos disponibles; vence ${new Date((automation.production_controls.expires_at ?? 0) * 1000).toLocaleString("es-ES", { timeZone: "Atlantic/Canary" })}.` : "No se puede confirmar la autorización productiva de estos controles. Desactivar una opción previamente activa sigue permitido por la API."}</p> : null}
            <article className="automation-state">
              <div className="automation-state-heading">
                <span className={`environment-dot ${automation?.last_error_code ? "dot-error" : ""}`} />
                <div><strong>{automation?.running ? "Descargando ahora" : automation?.enabled ? "Programación activa" : "Programación detenida"}</strong><small>Consulta segura cada {automation?.interval_minutes ?? 30} minutos</small></div>
                {automation ? <span className={`soft-chip ${automation.last_error_code ? "chip-err" : "chip-ok"}`}>{automation.last_error_code ? "Con errores" : "Sin errores"}</span> : null}
              </div>
              <dl>
                <div><dt>Último intento</dt><dd>{formatOptionalDate(automation?.last_attempt_at ?? null)}</dd></div>
                <div><dt>Último éxito</dt><dd>{formatOptionalDate(automation?.last_success_at ?? null)}</dd></div>
                <div><dt>Fallos consecutivos</dt><dd>{automation?.consecutive_failures ?? 0}</dd></div>
                <div><dt>Estado</dt><dd>{automation?.last_error_code || "Sin errores"}</dd></div>
              </dl>
            </article>
            <article className="latest-catalog">
              <p className="eyebrow">Último catálogo válido</p>
              {automation?.last_upload ? <>
                <h3>{automation.last_upload.original_name}</h3>
                <dl>
                  <div><dt>Filas</dt><dd>{automation.last_upload.row_count.toLocaleString("es-ES", COUNT_FORMAT)}</dd></div>
                  <div><dt>Tamaño</dt><dd>{(automation.last_upload.byte_size / 1024 / 1024).toFixed(2)} MiB</dd></div>
                  <div><dt>Codificación</dt><dd>{automation.last_upload.encoding}</dd></div>
                  <div><dt>SHA-256</dt><dd className="hash-value">{automation.last_upload.sha256.slice(0, 16)}…</dd></div>
                </dl>
              </> : <p>Esperando la primera descarga válida desde iDirecto.</p>}
            </article>
            <article className="latest-catalog price-snapshot-card">
              <div className="automation-state-heading">
                <span className={`environment-dot ${automation?.price_snapshot_health.status === "critical" ? "dot-error" : automation?.price_snapshot_health.status === "warning" ? "dot-warning" : ""}`} />
                <div>
                  <strong>{automation?.price_snapshot_running ? "Capturando precios ahora" : automation?.price_snapshot_enabled ? "Snapshot de precios programado" : "Captura de precios detenida"}</strong>
                  <small>Solo lectura de PVP base cada {automation?.price_snapshot_interval_hours ?? 6} horas</small>
                </div>
                {automation ? <span className={`soft-chip ${{ healthy: "chip-ok", warning: "chip-warn", critical: "chip-err", disabled: "" }[automation.price_snapshot_health.status]}`}>{automation.price_snapshot_health.status === "healthy" ? "Correcta" : automation.price_snapshot_health.status === "warning" ? "Atención: envejeciendo" : automation.price_snapshot_health.status === "critical" ? `Alerta: ${automation.price_snapshot_health.code}` : "Desactivada"}</span> : null}
              </div>
              <dl>
                <div><dt>Último intento</dt><dd>{formatOptionalDate(automation?.price_snapshot_last_attempt_at ?? null)}</dd></div>
                <div><dt>Último éxito</dt><dd>{formatOptionalDate(automation?.price_snapshot_last_success_at ?? null)}</dd></div>
                <div><dt>Fallos consecutivos</dt><dd>{automation?.price_snapshot_consecutive_failures ?? 0}</dd></div>
                <div><dt>Productos capturados</dt><dd>{automation?.current_price_snapshot?.row_count.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</dd></div>
                <div><dt>Capturado</dt><dd>{formatOptionalDate(automation?.current_price_snapshot?.captured_at ?? null)}</dd></div>
                <div><dt>Base</dt><dd>PVP catálogo con impuestos</dd></div>
                <div><dt>SHA-256</dt><dd className="hash-value">{automation?.current_price_snapshot ? `${automation.current_price_snapshot.sha256.slice(0, 16)}…` : "—"}</dd></div>
                <div><dt>Retención</dt><dd>{automation ? `${automation.price_snapshot_retention.days} días · mínimo ${automation.price_snapshot_retention.minimum_keep}` : "—"}</dd></div>
              </dl>
              {automation?.price_snapshot_health.status === "warning" || automation?.price_snapshot_health.status === "critical" ? <p className={`snapshot-alert ${automation.price_snapshot_health.status}`} role="alert">{automation.price_snapshot_health.status === "critical" ? "La referencia de precios requiere intervención: hay un fallo o ha superado el doble del intervalo esperado." : "La referencia de precios se acerca al límite de frescura; comprueba la siguiente ejecución."}</p> : null}
              <p className="readonly-note">Cuenta SQL exclusiva de lectura. No consulta promociones ni precios por cliente. La retención nunca elimina el snapshot vigente ni evidencias de simulación.</p>
            </article>
            <article className="automation-state automatic-approval-card">
              <p className="eyebrow">Gate de aprobación</p>
              <label className="automation-toggle" htmlFor="automatic-approval-enabled">
                <input
                  id="automatic-approval-enabled"
                  type="checkbox"
                  checked={automation?.automatic_approval_enabled ?? false}
                  disabled={(automationControlsBlocked && !automation?.automatic_approval_enabled) || !user.isStaff || automationControlBusy || !automation}
                  onChange={(event) => void changeAutomaticApproval(event.target.checked)}
                />
                <span>
                  <strong>Omitir aprobaciones manuales</strong>
                  <small>{automationControlBusy ? "Guardando…" : "Al seleccionarlo, autoriza aprobar el plan exacto cuando supera los controles técnicos y de datos."}</small>
                </span>
              </label>
              <p className="readonly-note">Los límites comerciales, la frescura, el dry-run y las reconciliaciones siguen siendo obligatorios. Si alguno falla, el ciclo se detiene.</p>
            </article>
            <article className={`automation-state complete-cycle-card${automation?.complete_cycle_blocked ? " scheduler-blocked" : ""}`}>
              <div className="automation-state-heading">
                <span className={`environment-dot ${automation?.complete_cycle_blocked ? "dot-error" : ""}`} />
                <div>
                  <strong>{automation?.complete_cycle_blocked ? "Programación bloqueada" : automation?.complete_cycle_enabled ? "Ciclo completo programado" : "Ciclo completo detenido"}</strong>
                  <small>{productionProfile ? "Usa el ejecutor productivo, sin solapes y dentro del cupo y caducidad autorizados." : "Descarga, valida, aplica, descarga imágenes y reconcilia en TEST."}</small>
                </div>
              </div>
              <label className="automation-toggle" htmlFor="complete-cycle-enabled">
                <input
                  id="complete-cycle-enabled"
                  type="checkbox"
                  checked={automation?.complete_cycle_enabled ?? false}
                  disabled={(automationControlsBlocked && !automation?.complete_cycle_enabled) || !user.isStaff || automationControlBusy || !automation}
                  onChange={(event) => void changeCompleteCycle({ enabled: event.target.checked })}
                />
                <span>
                  <strong>Ejecutar ciclos completos automáticamente</strong>
                  <small>{automationControlBusy ? "Guardando…" : "Al activarlo, el primer ciclo se pondrá en cola de inmediato."}</small>
                </span>
              </label>
              <label className="scheduler-interval" htmlFor="complete-cycle-interval">
                <span>Intervalo tras completar un ciclo</span>
                <select
                  id="complete-cycle-interval"
                  value={automation?.complete_cycle_interval_hours ?? 6}
                  disabled={automationControlsBlocked || !user.isStaff || automationControlBusy || !automation}
                  onChange={(event) => void changeCompleteCycle({ intervalHours: Number(event.target.value) })}
                >
                  <option value={1}>1 hora</option>
                  <option value={3}>3 horas</option>
                  <option value={6}>6 horas</option>
                  <option value={12}>12 horas</option>
                  <option value={24}>24 horas</option>
                  <option value={48}>48 horas</option>
                </select>
              </label>
              <dl>
                <div><dt>Próximo ciclo</dt><dd>{automation?.complete_cycle_blocked ? "Requiere intervención" : formatOptionalDate(automation?.complete_cycle_next_run_at ?? null)}</dd></div>
                <div><dt>Último intento</dt><dd>{formatOptionalDate(automation?.complete_cycle_last_attempt_at ?? null)}</dd></div>
                <div><dt>Último éxito</dt><dd>{formatOptionalDate(automation?.complete_cycle_last_success_at ?? null)}</dd></div>
                <div><dt>Fallos consecutivos</dt><dd>{automation?.complete_cycle_consecutive_failures ?? 0}</dd></div>
                <div><dt>Estado</dt><dd>{automation?.complete_cycle_last_error_code || "Sin errores"}</dd></div>
              </dl>
              {!automation?.automatic_approval_enabled && automation?.complete_cycle_enabled ? <p className="snapshot-alert warning" role="status">El ciclo se detendrá en cada simulación hasta recibir aprobación manual.</p> : null}
              <p className="readonly-note">Nunca solapa dos ciclos. Los fallos transitorios usan espera progresiva; los fallos de política bloquean nuevos intentos. Desactivarlo no corta un ciclo ya iniciado.</p>
            </article>
          </div>
        </section> : null}

        {activeSection === "#actualizaciones" ? <section id="actualizaciones" className="updates-page route-panel">
          <div className="panel-heading updates-heading">
            <p className="muted">{advancedUpdates ? "Vista avanzada · incluye detalle técnico, identificadores y ejecuciones anteriores." : "Vista simple · estado, siguiente acción y avisos."}</p>
            <div className="updates-view-actions">
              <button type="button" className="secondary-button" aria-expanded={advancedUpdates} aria-controls="updates-advanced" onClick={() => setAdvancedUpdates(value => !value)}>
                {advancedUpdates ? "Vista simple" : "Información avanzada"}
              </button>
              <button className="secondary-button" onClick={() => void refresh()}>Actualizar</button>
            </div>
          </div>
          <article className={`update-control-card update-control-${run?.status ?? "idle"}`} aria-live="polite">
            <div className="update-control-heading">
              <div>
                <p className="eyebrow">Ciclo actual · {environmentLabel}</p>
                <span className={`image-state image-state-${controlSnapshot.state === "ready" ? cycleStateClass(run) : "idle"}`}>
                  {controlView.label}
                </span>
                <h3>{controlView.title}</h3>
                <p>{controlView.message}</p>
                {advancedUpdates && run ? <small className="update-trigger">Inicio {run.trigger === "automatic" ? "automático" : "manual"} · {formatDate(run.created_at)} · ciclo {run.id.slice(0, 8)} · change set {run.change_set_id?.slice(0, 8) ?? "pendiente"}</small> : null}
              </div>
              <p className={`control-freshness${controlSnapshot.state === "error" ? " stale" : ""}`}><ClockIcon />{freshnessText}</p>
            </div>
            {controlSnapshot.state === "error" ? <p role="status" className="snapshot-alert warning">No se pudo consultar el estado del ciclo. {controlSnapshot.updatedAt ? `Se conserva la última respuesta de ${formatDate(controlSnapshot.updatedAt)}; puede estar desactualizada.` : "Todavía no hay una respuesta válida."} Los controles permanecerán bloqueados hasta recuperar la conexión. Los demás indicadores siguen consultándose.</p> : null}
            {updateControl.runtime && !updateControl.runtime.ready ? <div className="snapshot-alert warning" role="status">
              <strong>Motor de actualización no disponible</strong>
              <p>No se puede confirmar que todos los ejecutores estén preparados. El ciclo no avanzará con normalidad; no es un fallo de conexión del navegador.</p>
              {advancedUpdates && updateControl.runtime.components.map(component => <div key={component.label}>{component.label}: {component.ready ? "Preparado" : "No disponible"}</div>)}
            </div> : null}
            {nextAction ? <div className={`next-action tone-${nextAction.tone}`}>
              <strong>{nextAction.title}</strong>
              <p>{nextAction.text}</p>
            </div> : null}
            <div>
              <div className="image-progress-label">
                <strong>Progreso total · ciclo actual</strong>
                <span>{controlView.percent === undefined ? "—" : `${controlView.percent.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %`}</span>
              </div>
              <progress max={100} value={controlView.percent} aria-label="Progreso total del ciclo actual">
                {controlView.percent === undefined ? "Consultando estado" : `${controlView.percent} %`}
              </progress>
              <p className="card-note">Avance por fases, no por tiempo. Puede quedarse fijo un rato mientras el trabajo continúa.</p>
            </div>
            <dl className="cycle-timing" aria-label="Tiempos del ciclo actual">
              <div><dt>Tiempo transcurrido</dt><dd>{formatDuration(cycleTiming.elapsed)}</dd><small>Desde el inicio; incluye esperas y pausas.</small></div>
              <div><dt>Tiempo restante estimado</dt><dd>{cycleTiming.remaining === null ? "Por determinar" : formatDuration(cycleTiming.remaining)}</dd><small>{cycleTiming.note}</small></div>
            </dl>
            {run && steps ? <ol className="cycle-steps" aria-label="Fases del ciclo actual">
              {CYCLE_STEPS.map((step, index) => <li key={step.label} className={`cycle-step step-${steps[index]}`} aria-current={steps[index] === "current" ? "step" : undefined}>
                <span className="step-bar" aria-hidden="true" />
                <strong>{step.label}</strong>
                <span className="step-state">{stepStateLabel(steps[index], run, controlSnapshot.state === "ready")}</span>
              </li>)}
            </ol> : null}
            <fieldset className="update-control-actions" disabled={controlView.actionsBlocked} style={{ border: 0, padding: 0, minWidth: 0 }} aria-label="Acciones del ciclo">
              {run?.production_plan?.can_approve ? <div className="control-action">
                <p>Plan de producción: {run.production_plan.summary.rows ?? 0} productos; {run.production_plan.summary.creates ?? 0} altas, {run.production_plan.summary.updates ?? 0} actualizaciones y {run.production_plan.summary.protected ?? 0} protegidos omitidos.</p>
                <small>Filtrados: {run.production_plan.summary.filtered ?? 0}. Cuarentena de datos: {run.production_plan.summary.quarantined ?? 0}. Plan SHA256: {run.production_plan.sha256}</small>
                <button type="button" className="primary-button" disabled={updateControlBusy || !user.isStaff} onClick={() => void controlCatalogUpdate("approve")}>Aprobar catálogo exacto y continuar</button>
              </div> : null}
              {run?.environment === "production" && run.status === "succeeded" ? <button type="button" className="primary-button" disabled={startDisabled} onClick={() => void controlCatalogUpdate("start")}>Iniciar otra actualización supervisada</button> : null}
              {!run ? <div className="control-action">
                <button type="button" className="primary-button" disabled={startDisabled} aria-describedby="control-reason-start" onClick={() => void controlCatalogUpdate("start")}>
                  {updateControlBusy ? "Procesando…" : "Iniciar actualización"}
                </button>
                <small id="control-reason-start">{actionReason(startDisabled, updateControl.active ? "Ya hay un ciclo activo." : updateControl.runtime?.ready === false ? "El motor no está preparado." : "No disponible en este estado.", "Descarga el CSV y prepara un ciclo nuevo.")}</small>
              </div> : null}
              <div className="control-action">
                <button type="button" className="secondary-button" disabled={pauseDisabled} aria-describedby="control-reason-pause" onClick={() => void controlCatalogUpdate("pause")}>
                  Detener de forma segura
                </button>
                <small id="control-reason-pause">{actionReason(pauseDisabled, "Sólo con el ciclo en curso.", "Pausa cooperativa en un punto seguro.")}</small>
              </div>
              <div className="control-action">
                <button type="button" className="secondary-button" disabled={resumeDisabled} aria-describedby="control-reason-resume" onClick={() => void controlCatalogUpdate("resume")}>
                  Reanudar
                </button>
                <small id="control-reason-resume">{actionReason(resumeDisabled, "Sólo con el ciclo en pausa.", "Continúa desde el último punto seguro.")}</small>
              </div>
              <div className="control-action">
                <button type="button" className="primary-button" disabled={retryDisabled} aria-describedby="control-reason-retry" onClick={() => void controlCatalogUpdate("retry")}>
                  Reintentar actualización
                </button>
                <small id="control-reason-retry">{actionReason(retryDisabled, "Sólo si el ciclo ha fallado.", "Conserva el ciclo y su avance.")}</small>
              </div>
              <div className="control-action">
                <button type="button" className="secondary-button" disabled={restartDisabled} aria-describedby="control-reason-restart" onClick={() => void controlCatalogUpdate("restart")}>
                  Reiniciar desde cero…
                </button>
                <small id="control-reason-restart">{actionReason(restartDisabled, "No disponible en este estado.", "Crea un ciclo nuevo; pide confirmación.")}</small>
              </div>
            </fieldset>
            {advancedUpdates ? <small className="update-control-note">
              Reintentar conserva el ciclo y su avance. Reiniciar crea un ciclo nuevo; no borra el catálogo. Detener pausa en un punto seguro y permite reanudar. {" "}
              {run?.pause_mode === "after_phase"
                ? "La aplicación del catálogo no se interrumpe a mitad: la pausa será efectiva cuando termine esta fase."
                : "La pausa es cooperativa y conserva el último checkpoint; nunca corta una escritura monolítica a mitad."}
            </small> : null}
            {run?.control_action ? <p role="status">{run.control_action === "restart" ? "Reinicio solicitado" : run.control_action === "stop" ? "Detención solicitada" : "Reintento solicitado"}: esperando confirmación del ejecutor. No vuelva a pulsar.</p> : null}
            {run?.status === "failed" ? <p className="snapshot-alert warning">Reintentar no corrige un problema de red o DNS. Si persiste, el proceso puede volver a detenerse. Los fallos en fases de escritura requieren revisión técnica antes de repetirlas.</p> : null}
            {run?.status === "waiting_approval" && run.simulation_evidence_id ? <a className="row-action update-approval-link" href="#configuracion" onClick={() => setConfigurationFocus({ policyId: run.policy_id, evidenceId: run.simulation_evidence_id! })}>Revisar la simulación exacta del ciclo</a> : null}
            {updateControl.runtime?.ready ? <p className="runtime-line">
              <strong>Motor de actualización preparado</strong>
              <span>Iniciar, detener y reanudar se gestionan desde este panel. Los ejecutores atienden las solicitudes automáticamente.</span>
              {advancedUpdates && updateControl.runtime.components.map(component => <span key={component.label}>{component.label}: {component.ready ? "Preparado" : "No disponible"}</span>)}
            </p> : null}
          </article>
          <div className="pair-grid">
            {run ? <section className="card-block cycle-results" aria-label="Resultados confirmados del ciclo actual">
              <p className="eyebrow">Ciclo actual{run.change_set_id ? ` · change set ${run.change_set_id.slice(0, 8)}` : ""}</p>
              <h2>Resultados confirmados hasta ahora</h2>
              {imagesForCurrentCycle && imageStatus.available ? <>
                <dl className="mini-kv four">
                  <div><dt>Imágenes correctas</dt><dd>{(imageStatus.counts.created + imageStatus.counts.replaced + imageStatus.counts.existing_verified).toLocaleString("es-ES", COUNT_FORMAT)}</dd><small>nuevas o ya verificadas</small></div>
                  <div><dt>En cuarentena</dt><dd>{imageStatus.counts.quarantined_total.toLocaleString("es-ES", COUNT_FORMAT)}</dd><small>no son descargas correctas</small></div>
                  <div><dt>Omitidas</dt><dd>{(imageStatus.counts.protected_skipped ?? 0).toLocaleString("es-ES", COUNT_FORMAT)}</dd><small>por protección manual</small></div>
                  <div><dt>Pendientes</dt><dd>{Math.max(0, imageStatus.progress.total_items - imageStatus.progress.completed_items).toLocaleString("es-ES", COUNT_FORMAT)}</dd><small>de {imageStatus.progress.total_items.toLocaleString("es-ES", COUNT_FORMAT)} asignaciones</small></div>
                </dl>
                <p className="card-note">Contadores de imágenes confirmados al cerrar cada lote.</p>
              </> : <p className="card-note">El detalle de imágenes de este ciclo todavía no está publicado.</p>}
              {steps && steps.some(state => state !== "done") ? <>
                <p className="pending-title">Aún sin confirmar en este ciclo:</p>
                <ul className="pending-list">{CYCLE_STEPS.filter((_, index) => steps[index] !== "done").map(step => <li key={step.label}>{step.label}</li>)}</ul>
              </> : steps ? <p className="card-note">Todas las fases del ciclo están completadas.</p> : null}
            </section> : null}
            <CycleWarnings run={run} known={controlView.known} />
          </div>
          {!advancedUpdates && controlView.known && imageStatus.available && !imagesForCurrentCycle ? <details className="updates-detail-group previous-run">
            <summary>{run ? "Resultados de una ejecución anterior" : "Resultados de la última ejecución publicada"} <span className="soft-chip">change set {imageStatus.change_set}{run ? " · no es este ciclo" : ""}</span></summary>
            <p className="card-note">{run ? "El publicador de imágenes todavía informa de otra ejecución. Estos contadores no se suman al ciclo actual y se sustituirán cuando se publique su detalle." : "Contadores publicados por el proceso de imágenes de la última ejecución."}</p>
            <div className="image-quality-summary">
              <span><strong>{(imageStatus.counts.created + imageStatus.counts.replaced + imageStatus.counts.existing_verified).toLocaleString("es-ES", COUNT_FORMAT)}</strong> correctas (nuevas o ya verificadas)</span>
              <span><strong>{imageStatus.counts.source_cache_verified?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</strong> reutilizadas desde caché verificada</span>
              <span><strong>{imageStatus.counts.quarantined_total.toLocaleString("es-ES", COUNT_FORMAT)}</strong> en cuarentena · no son descargas correctas</span>
              <span><strong>{Math.max(0, imageStatus.progress.total_items - imageStatus.progress.completed_items).toLocaleString("es-ES", COUNT_FORMAT)}</strong> pendientes</span>
            </div>
            <p className="card-note">{imageStatus.workflow.state === "succeeded" && imageStatus.counts.quarantined_total > 0 ? "Completado con incidencias" : imageStateLabel(imageStatus.workflow.state)} · actualizado {formatDate(imageStatus.updated_at)}. Una cuarentena conserva el producto y su imagen anterior, si existe.</p>
          </details> : null}
          <div id="updates-advanced" hidden={!advancedUpdates}>
          {imageStatus.available ? (
            <article className="image-progress-card" aria-live="polite" aria-atomic="false">
              <div className="image-progress-heading">
                <div>
                  <p className="eyebrow">Progreso por fase · TEST</p>
                  <h3>{!controlView.known ? "Detalle de imágenes · ciclo pendiente de confirmar" : updateControl.run?.change_set_id?.slice(0, 8) === imageStatus.change_set ? "Detalle del ciclo actual" : "Detalle de una ejecución anterior"}</h3>
                  <small>Change set {imageStatus.change_set} · inicio observado {formatDate(imageStatus.workflow.observed_started_at)} · actualizado {formatDate(imageStatus.updated_at)}</small>
                </div>
                <span className={`image-state image-state-${imageStatus.workflow.state}`}>
                  {imageStatus.workflow.state === "succeeded" && imageStatus.counts.quarantined_total > 0 ? "Completado con incidencias" : imageStateLabel(imageStatus.workflow.state)}
                </span>
              </div>
              {controlView.known && updateControl.run?.change_set_id?.slice(0, 8) !== imageStatus.change_set ? <p className="progress-notice">Estos contadores pertenecen a {imageStatus.change_set}, no al ciclo indicado arriba. Se sustituirán cuando se publique el detalle del ciclo actual.</p> : null}
              <div className="image-progress-label">
                <strong>Total de esta ejecución · {imageStatus.workflow.phases.filter(phase => phase.state === "succeeded").length} de 5 fases completas</strong>
                <span className="progress-total-value">{imageStatus.workflow.percent.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %</span>
              </div>
              <progress max={100} value={imageStatus.workflow.percent} aria-label="Progreso completo de la actualización del catálogo">
                {imageStatus.workflow.percent} %
              </progress>
              <p className="workflow-basis">Avance ponderado, no tiempo transcurrido: CSV 5 % · validación 10 % · catálogo 15 % · imágenes 65 % · reconciliación 5 %. Se conserva el avance al fallar o pausar.</p>
              {imageStatus.intermediate_reconciliation && imageStatus.passes.main.state === "succeeded" ? (
                <details className="updates-detail-group" open={imageStatus.intermediate_reconciliation.state === "running"}>
                  <summary>Comprobación intermedia de portadas</summary>
                <section className={`reconcile-progress reconcile-progress-${imageStatus.intermediate_reconciliation.state}`} aria-labelledby="cover-validation-title">
                  <div className="reconcile-progress-heading">
                    <div><p className="eyebrow">Entre portadas y galería · sólo lectura</p>
                      <h4 id="cover-validation-title">Validación de las portadas descargadas</h4></div>
                    <span className={`image-state image-state-${imageStatus.intermediate_reconciliation.state}`}>{imageStateLabel(imageStatus.intermediate_reconciliation.state)}</span>
                  </div>
                  <p>{imageStatus.intermediate_reconciliation.state === "running"
                    ? "Ahora está comprobando las portadas en PrestaShop. La galería adicional espera a que termine esta validación; no es una descarga bloqueada."
                    : imageStatus.intermediate_reconciliation.state === "succeeded"
                      ? "Portadas verificadas. La galería adicional ya puede continuar."
                      : "La galería adicional requiere que esta validación termine correctamente."}</p>
                  <div className="image-progress-label">
                    <strong>{imageStatus.intermediate_reconciliation.completed_chunks.toLocaleString("es-ES", COUNT_FORMAT)} / {imageStatus.intermediate_reconciliation.total_chunks.toLocaleString("es-ES", COUNT_FORMAT)} lotes comprobados</strong>
                    <span>{imageStatus.intermediate_reconciliation.percent.toLocaleString("es-ES")} %</span>
                  </div>
                  <progress max={100} value={imageStatus.intermediate_reconciliation.percent} aria-label="Validación intermedia de portadas">{imageStatus.intermediate_reconciliation.percent} %</progress>
                  <dl className="reconcile-progress-metrics">
                    <div><dt>Tiempo transcurrido</dt><dd>{formatDuration(imageStatus.intermediate_reconciliation.elapsed_seconds)}</dd></div>
                    <div><dt>Tiempo restante estimado</dt><dd>{formatDuration(imageStatus.intermediate_reconciliation.eta_seconds)}</dd></div>
                    <div><dt>Último avance confirmado</dt><dd>{imageStatus.intermediate_reconciliation.updated_at ? formatDate(imageStatus.intermediate_reconciliation.updated_at) : "Esperando el primer checkpoint"}</dd></div>
                  </dl>
                  <small>El porcentaje global cuenta asignaciones de imágenes: no aumenta al volver a verificarlas. El avance de esta comprobación se muestra en su propia barra.</small>
                </section>
                </details>
              ) : null}
              {imageStatus.counts.quarantined_total > 0 ? <p className="progress-notice">{imageStatus.counts.quarantined_total.toLocaleString("es-ES", COUNT_FORMAT)} asignaciones de imagen con incidencias. No bloquean la actualización: se conservan las imágenes anteriores cuando existen. Los HTTP 403 aplazan nuevos intentos por URL durante 1, 2, 4 y hasta 7 días; se reintentan en un ciclo posterior al vencimiento.</p> : null}
              {imageStatus.worker.state === "failed" ? <section className="progress-failure" role="status">
                <strong>Descarga detenida · el avance anterior se conserva</strong>
                <p>{imageStatus.failure
                  ? `Galería, lote ${imageStatus.failure.chunk_index + 1}: ${imageStatus.failure.quarantines} de ${imageStatus.failure.assignments} imágenes en cuarentena; máximo permitido ${imageStatus.failure.maximum_quarantines}. ${imageStatus.failure.valid_images} válidas en ese intento, todavía no aplicadas.`
                  : "El worker de imágenes ha fallado. Consulta Logs para identificar la causa; no se han completado todas las imágenes."}</p>
                {imageStatus.failure ? <small>{imageStatus.failure.code}{Object.entries(imageStatus.failure.http_status_counts).map(([code, count]) => ` · HTTP ${code}: ${count}`).join("")}</small> : null}
                <a href="#logs">Consultar Logs</a>
              </section> : null}
              <details className="updates-detail-group" open>
              <summary>1. Progreso de cada fase</summary>
              <ol className="workflow-phases" aria-label="Fases de actualización">
                {imageStatus.workflow.phases.map((phase, index) => (
                  <li className={`workflow-phase workflow-phase-${phase.state}`} key={phase.key}>
                    <span className="workflow-phase-index" aria-hidden="true">{index + 1}</span>
                    <div>
                      <strong>{WORKFLOW_PHASE_LABELS[phase.key]}</strong>
                      <small>{phase.weight} % del total{phase.key === "images" ? ` · ${imageStatus.progress.completed_items.toLocaleString("es-ES", COUNT_FORMAT)} / ${imageStatus.progress.total_items.toLocaleString("es-ES", COUNT_FORMAT)} asignaciones procesadas` : ""}</small>
                    </div>
                    <div className="workflow-phase-meter">
                      <div><strong>{phase.percent.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %</strong><span>{imageStateLabel(phase.state)}</span></div>
                      <progress max={100} value={phase.percent} aria-label={`Paso ${index + 1}: ${WORKFLOW_PHASE_LABELS[phase.key]}`}>{phase.percent} %</progress>
                    </div>
                  </li>
                ))}
              </ol>
              </details>
              <details className="updates-detail-group">
              <summary>2. Imágenes e incidencias</summary>
              <div className="image-detail-progress">
                <div className="image-progress-label">
                  <strong>Asignaciones de imagen procesadas (incluye cuarentenas)</strong>
                  <span>{imageStatus.progress.percent.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %</span>
                </div>
                <progress max={imageStatus.progress.total_items} value={imageStatus.progress.completed_items} aria-label="Progreso de descarga de imágenes">
                  {imageStatus.progress.percent} %
                </progress>
                <div className="image-pass-grid">
                  {(["main", "gallery"] as const).map(key => {
                    const pass = imageStatus.passes[key];
                    const percent = pass.total_items ? pass.completed_items / pass.total_items * 100 : 0;
                    const title = key === "main" ? "Portadas · primero" : "Galería adicional · después";
                    return <section key={key} className="image-pass">
                      <h4>{title}</h4>
                      <div className="image-progress-label"><strong>{pass.completed_items.toLocaleString("es-ES", COUNT_FORMAT)} / {pass.total_items.toLocaleString("es-ES", COUNT_FORMAT)}</strong><span>{percent.toLocaleString("es-ES", { maximumFractionDigits: 1 })} %</span></div>
                      <progress max={100} value={percent} aria-label={title}>{percent} %</progress>
                      <small>{pass.total_items ? imageStateLabel(pass.state) : "Sin asignaciones en el perfil disponible"}</small>
                    </section>;
                  })}
                </div>
                <div className="image-quality-summary">
                  <span><strong>{(imageStatus.counts.created + imageStatus.counts.replaced + imageStatus.counts.existing_verified).toLocaleString("es-ES", COUNT_FORMAT)}</strong> correctas (nuevas o ya verificadas)</span>
                  <span><strong>{imageStatus.counts.quarantined_total.toLocaleString("es-ES", COUNT_FORMAT)}</strong> en cuarentena, no son descargas correctas</span>
                  {(imageStatus.counts.protected_skipped ?? 0) > 0 ? <span><strong>{imageStatus.counts.protected_skipped?.toLocaleString("es-ES", COUNT_FORMAT)}</strong> omitidas por protección manual; imágenes originales conservadas</span> : null}
                  <span><strong>{Math.max(0, imageStatus.progress.total_items - imageStatus.progress.completed_items).toLocaleString("es-ES", COUNT_FORMAT)}</strong> pendientes de procesar</span>
                </div>
                <p className="workflow-basis">Contadores confirmados al cerrar cada lote. Una cuarentena conserva el producto y su imagen anterior, si existe; no implica que toda su galería esté disponible.</p>
                {imageStatus.counts.quarantined_total > 0 ? <section aria-label="Desglose de incidencias de imágenes">
                  <h4>¿Por qué quedan imágenes pendientes?</h4>
                  <div className="image-quality-summary">
                    <span><strong>{imageStatus.counts.retry_deferred?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</strong> aplazadas por espera de reintento · sin petición de red</span>
                    <span><strong>{imageStatus.counts.pending_main_cover?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</strong> adicionales pendientes de su portada</span>
                    <span><strong>{imageStatus.counts.source_failure?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</strong> incidencias de origen observadas al preparar el lote</span>
                  </div>
                  <p className="workflow-basis">Categorías excluyentes: si falta la portada, prevalece esa dependencia. {(imageStatus.counts.incidents_unmeasured ?? imageStatus.counts.quarantined_total) > 0 ? `${(imageStatus.counts.incidents_unmeasured ?? imageStatus.counts.quarantined_total).toLocaleString("es-ES", COUNT_FORMAT)} incidencias de lotes anteriores sin este desglose; no se reconstruye su causa.` : "Todas las incidencias de los lotes terminados están clasificadas."}</p>
                </section> : null}
              </div>
              </details>
              <details className="updates-detail-group" open={imageStatus.reconciliation.state === "running"}>
              <summary>3. Reconciliación final</summary>
              <section className={`reconcile-progress reconcile-progress-${imageStatus.reconciliation.state}`} aria-labelledby="reconcile-progress-title">
                <div className="reconcile-progress-heading">
                  <div>
                    <p className="eyebrow">Validación final · sólo lectura</p>
                    <h4 id="reconcile-progress-title">Reconciliación de {imageStatus.reconciliation.pass === "gallery" ? "galería" : "portadas"}</h4>
                  </div>
                  <span className={`image-state image-state-${imageStatus.reconciliation.state}`}>{imageStateLabel(imageStatus.reconciliation.state)}</span>
                </div>
                <div className="image-progress-label">
                  <strong>{imageStatus.reconciliation.completed_chunks.toLocaleString("es-ES", COUNT_FORMAT)} / {imageStatus.reconciliation.total_chunks.toLocaleString("es-ES", COUNT_FORMAT)} lotes</strong>
                  <span>{imageStatus.reconciliation.percent.toLocaleString("es-ES", { maximumFractionDigits: 2 })} %</span>
                </div>
                <progress max={100} value={imageStatus.reconciliation.percent} aria-label={`Reconciliación de ${imageStatus.reconciliation.pass === "gallery" ? "galería" : "portadas"}`}>
                  {imageStatus.reconciliation.percent} %
                </progress>
                <dl className="reconcile-progress-metrics">
                  <div><dt>Productos o asignaciones</dt><dd>{imageStatus.reconciliation.completed_items.toLocaleString("es-ES", COUNT_FORMAT)} / {imageStatus.reconciliation.total_items.toLocaleString("es-ES", COUNT_FORMAT)}</dd></div>
                  <div><dt>Tiempo transcurrido</dt><dd>{formatDuration(imageStatus.reconciliation.elapsed_seconds)}</dd></div>
                  <div><dt>Tiempo restante estimado</dt><dd>{formatDuration(imageStatus.reconciliation.eta_seconds)}</dd></div>
                  <div><dt>Media por lote</dt><dd>{formatDuration(imageStatus.reconciliation.average_chunk_seconds)}</dd></div>
                </dl>
                <small className="reconcile-progress-note">{
                  (imageStatus.reconciliation.pass === "gallery" ? imageStatus.passes.gallery.state : imageStatus.passes.main.state) === "failed"
                    ? "Bloqueada por un fallo en la descarga previa; consulta Logs · writes=0"
                    : imageStatus.reconciliation.updated_at
                      ? `Último checkpoint ${formatDate(imageStatus.reconciliation.updated_at)} · writes=0`
                      : "Esperando el primer checkpoint del reconciliador · writes=0"
                }</small>
              </section>
              </details>
              <details className="updates-detail-group">
              <summary>4. Métricas técnicas, tiempos y almacenamiento</summary>
              <div className="image-progress-metrics">
                <div><span>Portadas</span><strong>{imageStatus.passes.main.completed_items.toLocaleString("es-ES", COUNT_FORMAT)} / {imageStatus.passes.main.total_items.toLocaleString("es-ES", COUNT_FORMAT)}</strong><small>{imageStateLabel(imageStatus.passes.main.state)} · siempre primero</small></div>
                <div><span>Galería adicional</span><strong>{imageStatus.passes.gallery.completed_items.toLocaleString("es-ES", COUNT_FORMAT)} / {imageStatus.passes.gallery.total_items.toLocaleString("es-ES", COUNT_FORMAT)}</strong><small>{imageStatus.passes.gallery.total_items === 0 ? "Esperando perfil sellado" : `${imageStateLabel(imageStatus.passes.gallery.state)} · después de portadas`}</small></div>
                <div><span>Lotes terminados</span><strong>{imageStatus.progress.completed_chunks} / {imageStatus.progress.total_chunks}</strong><small>{imageStatus.progress.current_chunk === null ? "Sin lote activo" : `${imageStatus.progress.current_pass === "gallery" ? "Galería" : "Portadas"}: lote ${imageStatus.progress.current_chunk + 1}`}</small></div>
                <div><span>Nuevas / reemplazadas en la tienda</span><strong>{imageStatus.counts.created.toLocaleString("es-ES", COUNT_FORMAT)} / {imageStatus.counts.replaced.toLocaleString("es-ES", COUNT_FORMAT)}</strong><small>{imageStatus.counts.existing_verified.toLocaleString("es-ES", COUNT_FORMAT)} ya existían en PrestaShop; no indica descargas evitadas</small></div>
                <div><span>Descargadas / reutilizadas desde caché</span><strong>{imageStatus.counts.source_downloaded?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"} / {imageStatus.counts.source_cache_verified?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</strong><small>Imágenes válidas de lotes completados, no productos revisados. {(imageStatus.counts.source_unmeasured ?? 0) > 0 ? `${imageStatus.counts.source_unmeasured!.toLocaleString("es-ES", COUNT_FORMAT)} anteriores sin esta medición.` : "Las copias locales se verifican antes de reutilizarlas."}</small></div>
                <div><span>Ya instaladas · registro verificado</span><strong>{imageStatus.counts.source_registry_verified?.toLocaleString("es-ES", COUNT_FORMAT) ?? "—"}</strong><small>Sin descarga ni copia temporal; se verifican asociación, original y miniaturas de PrestaShop.</small></div>
                <div><span>Cuarentenas seguras</span><strong>{imageStatus.counts.quarantined_total.toLocaleString("es-ES", COUNT_FORMAT)}</strong><small>No desactivan el producto</small></div>
                <div><span>Tiempo restante estimado</span><strong>{formatDuration(imageStatus.timing.eta_seconds)}</strong><small>Media de aplicación {formatDuration(imageStatus.timing.average_apply_seconds)}</small></div>
                <div><span>Espacio libre · importación</span><strong>{(imageStatus.disk.free_bytes / 1024 ** 3).toLocaleString("es-ES", { maximumFractionDigits: 1 })} GiB</strong><small>Gate mínimo {(imageStatus.disk.minimum_bytes / 1024 ** 3).toLocaleString("es-ES", { maximumFractionDigits: 1 })} GiB</small></div>
                <div><span>Espacio libre · imágenes de la tienda</span><strong>{imageStatus.disk.shop_free_bytes == null ? "—" : (imageStatus.disk.shop_free_bytes / 1024 ** 3).toLocaleString("es-ES", { maximumFractionDigits: 1 })+" GiB"}</strong><small>Reserva independiente de {( (imageStatus.disk.shop_minimum_bytes ?? 3000000000) / 1024 ** 3).toLocaleString("es-ES", { maximumFractionDigits: 1 })} GiB; puede estar en otro disco.</small></div>
                <div><span>Reconciliación final</span><strong>{imageStateLabel(imageStatus.reconcile.state)}</strong><small>Solo lectura al terminar</small></div>
              </div>
              </details>
            </article>
          ) : (
            <div className="image-progress-unavailable">
              <strong>Progreso de imágenes pendiente</strong>
              <span>{imageStatus.reason === "worker_identity_mismatch"
                ? "Bloqueado: el proceso de imágenes pertenece a otro ciclo. No se atribuye su progreso a esta actualización."
                : imageStatus.reason === "worker_identity_unavailable"
                  ? "No se puede verificar la identidad del worker. Se bloquean nuevos ciclos por seguridad."
                  : imageStatus.reason === "invalid_status" ? "El snapshot local no es válido; el importador no se ha interrumpido." : "Esperando el primer snapshot local del importador."}</span>
            </div>
          )}
          {jobHistory}
          </div>
        </section> : null}
        {activeSection === "#mantenimiento" && user.isStaff && <Maintenance onSessionExpired={onSessionExpired} />}
        {activeSection === "#logs" ? <section id="logs" className="page-section observability-panel route-panel">
          <div className="panel-heading">
            <div><p className="eyebrow">Observabilidad ligera · {observability.window_days} días</p><h2>Tiempos de ejecución e incidencias</h2></div>
            <span className="test-badge">Sólo metadatos</span>
          </div>
          <p className="observability-note">El p99 marca el umbral del 1 % de ejecuciones más lentas. Para tiempos, cuanto menor sea mejor. El margen compara ese p99 con la cadencia programada.</p>
          <div className="observability-summary">
            <article><span>Mediciones correctas</span><strong>{observability.overall.duration.samples}</strong></article>
            <article><span>Tiempo medio</span><strong>{formatDuration(observability.overall.duration.average_seconds)}</strong></article>
            <article><span>P99 · 1 % más lento</span><strong>{formatDuration(observability.overall.duration.p99_seconds)}</strong></article>
            <article className={observability.overall.failed ? "metric-warning" : ""}><span>Tasa de fallo</span><strong>{observability.overall.failure_rate_percent.toLocaleString("es-ES")} %</strong>{observability.overall.failed ? <small><WarnIcon />Con fallos en la ventana</small> : null}</article>
          </div>
          <section className="card-block" aria-labelledby="operations-title">
          <div className="card-block-heading"><h2 id="operations-title">Tiempos por proceso</h2><small className="muted">Desplaza la tabla en horizontal si no cabe</small></div>
          {observability.operations.length ? <div className="table-wrap">
            <table className="performance-table">
              <thead><tr><th>Proceso</th><th>Muestras</th><th>Media</th><th>P50</th><th>P95</th><th>P99 / 1 % lento</th><th>Máximo</th><th>Margen</th></tr></thead>
              <tbody>{observability.operations.map((operation) => <tr key={operation.kind}>
                <td><strong>{operation.label}</strong><span className="job-id">{operation.failed} fallos · {operation.failure_rate_percent.toLocaleString("es-ES")} %</span></td>
                <td>{operation.duration.samples}</td>
                <td>{formatDuration(operation.duration.average_seconds)}</td>
                <td>{formatDuration(operation.duration.p50_seconds)}</td>
                <td>{formatDuration(operation.duration.p95_seconds)}</td>
                <td><strong>{formatDuration(operation.duration.p99_seconds)}</strong></td>
                <td>{formatDuration(operation.duration.maximum_seconds)}</td>
                <td className={operation.headroom_seconds !== null && operation.headroom_seconds < 0 ? "negative-headroom" : ""}>{operation.headroom_seconds === null ? "Manual" : `${formatDuration(operation.headroom_seconds)} libres`} {operation.p99_budget_percent !== null ? <small>{operation.p99_budget_percent.toLocaleString("es-ES")} % usado</small> : null}</td>
              </tr>)}</tbody>
            </table>
          </div> : <p className="empty-state">Todavía no hay ejecuciones finalizadas en la ventana.</p>}
          </section>
          <section className="card-block" aria-label="Registro de fallos">
          <div className="failure-log-heading"><div><p className="eyebrow">Registro de fallos</p><h3>Incidencias recientes</h3></div><small>Actualizado {formatDate(observability.generated_at)}</small></div>
          {observability.failures.length ? <div className="failure-log" role="log" aria-label="Registro de fallos">
            {observability.failures.map((entry) => <article key={`${entry.source}-${entry.id}`} className={`failure-entry ${entry.severity}`}>
              <span className="failure-marker" aria-hidden="true" />
              <div><strong>{entry.label}<span className={`soft-chip ${entry.severity === "critical" ? "chip-err" : "chip-warn"}`}>{entry.severity === "critical" ? "Crítico" : "Aviso"}</span></strong><code>{entry.error_code}</code></div>
              <div><span>{formatDate(entry.occurred_at)}</span><small>{entry.duration_seconds === null ? "Estado de salud" : `Duración ${formatDuration(entry.duration_seconds)}`}</small></div>
            </article>)}
          </div> : <div className="empty-log"><span>✓</span><div><strong>Sin fallos en la ventana</strong><small>Los errores de ejecución aparecerán aquí con su código y duración.</small></div></div>}
          </section>
        </section> : null}
        {(activeSection === "#resumen" || (activeSection === "#actualizaciones" && advancedUpdates)) && selectedJob?.kind === "catalog_profile" && selectedJob.result ? <QualityReport job={selectedJob} onClose={() => setSelectedJob(null)} /> : null}
        {(activeSection === "#resumen" || (activeSection === "#actualizaciones" && advancedUpdates)) && selectedJob?.kind === "catalog_rehearsal" && selectedJob.result ? <RehearsalReport job={selectedJob} onClose={() => setSelectedJob(null)} /> : null}
        {activeSection === "#ayuda" ? <Suspense fallback={<p className="empty-state" role="status">Cargando la ayuda…</p>}><HelpCenter productionProfile={productionProfile} /></Suspense> : null}
        {activeSection === "#configuracion" && user.isStaff ? <PolicyConfiguration key={configurationFocus?.evidenceId ?? "default"} focusPolicyId={configurationFocus?.policyId} focusEvidenceId={configurationFocus?.evidenceId} production={productionProfile} /> : null}
      </main>
    </div>
  );
}

function RehearsalReport({ job, onClose }: { job: PortalJob; onClose: () => void }) {
  const result = job.result as unknown as CatalogRehearsalResult;
  return <section className="panel quality-report" aria-label="Plan de ensayo mixto">
    <div className="panel-heading"><div><p className="eyebrow">Plan sellado · TEST</p><h2>Ensayo mixto preparado</h2></div><button className="secondary-button" onClick={onClose}>Cerrar</button></div>
    <div className="report-proof"><strong>{result.privacy.classification}</strong><span>No escribe en PrestaShop</span><code>{result.plan.mixed_plan_sha256.slice(0, 16)}…</code></div>
    <div className="report-metrics">
      <article><span>Categorías nuevas</span><strong>{result.plan.category_create_operations}</strong></article>
      <article><span>Productos</span><strong>{result.plan.product_operations}</strong></article>
      <article><span>Protegidas</span><strong>{result.plan.protected_records}</strong></article>
      <article><span>Fencing token</span><strong>{result.fencing_token}</strong></article>
    </div>
  </section>;
}

function QualityReport({ job, onClose }: { job: PortalJob; onClose: () => void }) {
  const profile = job.result as unknown as QualityProfile;
  const failures = Object.entries(profile.validity.contract_failure_occurrences).filter(([, value]) => value.count > 0).slice(0, 8);
  return <section className="panel quality-report" aria-label="Informe de calidad">
    <div className="panel-heading"><div><p className="eyebrow">Informe agregado</p><h2>{profile.source.name}</h2></div><button className="secondary-button" onClick={onClose}>Cerrar</button></div>
    <div className="report-proof"><strong>{profile.privacy.classification}</strong><span>No contiene filas de producto</span><code>{profile.source.sha256.slice(0, 16)}…</code></div>
    <div className="report-metrics">
      <article><span>Filas</span><strong>{profile.grain.rows.toLocaleString("es-ES", COUNT_FORMAT)}</strong></article>
      <article><span>Filas inválidas</span><strong>{profile.validity.invalid_rows_for_current_contract.count.toLocaleString("es-ES", COUNT_FORMAT)}</strong><small>{profile.validity.invalid_rows_for_current_contract.rate_percent}%</small></article>
      <article><span>Marcas</span><strong>{profile.dimensions.brands.distinct_normalized_nonblank}</strong></article>
      <article><span>Categorías</span><strong>{profile.dimensions.categories.distinct_path_keys}</strong></article>
    </div>
    <div className="report-columns"><div><h3>Parser</h3><p>{profile.parser.encoding} · separador {profile.parser.delimiter} · {profile.parser.column_count} columnas</p><p>{profile.grain.duplicate_sku_groups} grupos de SKU duplicados · {profile.grain.empty_rows} filas vacías</p></div><div><h3>Bloqueos principales</h3>{failures.length ? <ul>{failures.map(([code, value]) => <li key={code}><span>{code}</span><strong>{value.count}</strong></li>)}</ul> : <p>Sin fallos de contrato.</p>}</div></div>
  </section>;
}

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");

  useEffect(() => {
    getSession()
      .then(setUser)
      .catch(() => {
        setUser(null);
        setSessionError("No se pudo comprobar la sesión: el servidor no respondió correctamente. Inicia sesión de nuevo o inténtalo en unos minutos.");
      })
      .finally(() => setLoading(false));
  }, []);

  async function endSession() {
    await logout();
    setUser(null);
  }

  const handleSessionExpired = useCallback(() => setUser(null), []);

  if (loading) {
    return <main className="loading-screen"><BrandLogo /><p>Preparando el portal…</p></main>;
  }
  if (!user) {
    return <LoginScreen onAuthenticated={setUser} initialError={sessionError} />;
  }
  return <Dashboard user={user} onLogout={() => void endSession()} onSessionExpired={handleSessionExpired} />;
}
